import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import CabPage from "../CabPage";
import MobileNav from "../account/MobileNav";
import "../css/myloads.css";
import {
  Icon,
  P,
  fetchAllOrders,
  notExpired,
  cityOf,
  nf,
  fmtDateShort,
  fmtDateTime,
  parseDate,
  bodyTypeName,
  BODY_TYPE_OPTIONS,
  ORDER_TYPES,
  shortId,
  codeSign,
} from "../loads/common";
import { fetchMarket, fetchOffers, acceptOffer, extendLoad, revealContact, fetchTrips } from "../../utils/matchingApi";
import { Dialog, useToast } from "../fleet/ui";
import SidePanel, { SideRows } from "../ui/SidePanel";
import { PartyCard } from "../ui/cards";
import { apiError, patchOrder, fmtResponse } from "../loads/orderActions";

/**
 * «Мои грузы» — wireframe iTruck_My_Loads.html.
 * Orders: POST /order_search/ filtered by owner (same as legacy MyOrders).
 * Status mapping (backend OrderStatus): 1 published → Активен (or Закрыт when the
 * loading window has passed), 3 → В работе, 4 / delivery FINISHED → Завершен,
 * 5 → Черновик, 2 → Закрыт (removed by the author); a published load whose loading window or
 * matching TTL (12 h) is over → «Истёк срок». Both live in the «Закрытые» tab.
 * «Подходящих машин» = matching market (GET /m/orders/:id/market), «Откликов» =
 * offers (GET /m/orders/:id/offers); only fetched for published loads, 4 at a time.
 */
const TABS = [
  ["all", "Все грузы"],
  ["draft", "Черновики"],
  ["active", "Активные"],
  ["working", "В работе"],
  ["completed", "Завершенные"],
  ["closed", "Закрытые"], // closed by the author + TTL/date-expired
];
const STATUS_LABEL = { active: "Активен", working: "В работе", completed: "Завершен", draft: "Черновик", closed: "Закрыт", expired: "Истёк срок" };
const PAGE_SIZE = 25;
const isClosedTab = (st) => st === "closed" || st === "expired";
const MONTHS_NOM = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];

export const statusOf = (o) => {
  if (o.status === 5) return "draft";
  if (o.status === 2) return "closed";
  if (o.status === 4 || o.delivery_status === 7) return "completed";
  if (o.status === 3 || (o.delivery_status >= 1 && o.delivery_status <= 6)) return "working";
  return notExpired(o) ? "active" : "expired";
};

// market + offers → row counters and the load's TTL state (ТЗ §7.2: a load expires unless the author confirms it)
const countsOf = (m, of) => ({
  matches: m?.market?.matched_trucks ?? null,
  offers: of ? of.count ?? of.offers?.length ?? 0 : null,
  ttlExpired: Boolean(m?.market?.load_ttl_expired),
  ttlLeft: m?.load?.expires_in_minutes ?? null,
});

const periodKey = (o) => {
  const d = parseDate(o.created_at);
  return d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}` : "";
};

export default function MyloadsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const user = useSelector((s) => s.auth?.user);
  // legacy links use ?selected=draft|archive
  const legacy = params.get("selected");
  const wanted = params.get("tab") || (legacy === "draft" ? "draft" : "all");
  const tab = TABS.some(([k]) => k === wanted) ? wanted : "all";

  const [orders, setOrders] = useState(null);
  const [error, setError] = useState(null);
  const [counts, setCounts] = useState({}); // uuid -> {matches, offers}
  const [filters, setFilters] = useState({ query: params.get("q") || "", status: legacy === "archive" ? "closed" : "all", body: "all", period: "all" });
  const [selected, setSelected] = useState({});
  const [trips, setTrips] = useState({}); // load uuid -> accepted trip (who carries it)
  const [dialog, setDialog] = useState(null); // {k, o}
  const [busy, setBusy] = useState(false);
  const [toast, toastNode] = useToast();

  const load = useCallback(() => {
    if (!user?.id) return;
    return fetchAllOrders()
      .then((d) => setOrders(d.filter((o) => o.owner === user.id)))
      .catch(() => setError("Не удалось загрузить грузы"));
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  // market + offers counters for published loads, batched (4 in flight)
  useEffect(() => {
    if (!orders) return;
    // only loads that can still be matched need counters / TTL state (closed ones would just waste requests)
    const todo = orders.filter((o) => ["active", "working"].includes(statusOf(o))).slice(0, 60);
    let alive = true;
    let i = 0;
    const worker = async () => {
      while (alive && i < todo.length) {
        const o = todo[i++];
        const [m, of] = await Promise.all([fetchMarket(o.uuid).catch(() => null), fetchOffers(o.uuid).catch(() => null)]);
        if (!alive) return;
        setCounts((c) => ({ ...c, [o.uuid]: countsOf(m, of) }));
      }
    };
    Promise.all([worker(), worker(), worker(), worker()]);
    return () => {
      alive = false;
    };
  }, [orders]);

  // who carries the loads that are in work: my trips as the load owner (GET /m/trips)
  useEffect(() => {
    if (!user?.id) return undefined;
    let alive = true;
    fetchTrips("?limit=500")
      .then((r) => {
        if (!alive) return;
        const by = {};
        (r?.trips || [])
          .filter((t) => t.status !== "CANCELLED") // `as` may be "carrier" when I carry my own load
          .forEach((t) => {
            by[t.load_id] = t;
          });
        setTrips(by);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [user?.id, orders]);

  // wireframe: Enter in the global search copies the text into the page's search field
  useEffect(() => {
    const onSearch = (e) => {
      e.preventDefault();
      setFilters((f) => ({ ...f, query: e.detail?.q || "" }));
    };
    window.addEventListener("cab:global-search", onSearch);
    return () => window.removeEventListener("cab:global-search", onSearch);
  }, []);

  // TTL-expired loads are closed for matching (no matches, no pushes) — «Истёк срок», distinct from a load the author closed («Закрыт»)
  const effStatus = (o) => {
    const base = statusOf(o);
    return base === "active" && counts[o.uuid]?.ttlExpired ? "expired" : base;
  };
  const rows = useMemo(() => (orders || []).map((o) => ({ o, st: effStatus(o) })), [orders, counts]); // eslint-disable-line react-hooks/exhaustive-deps

  const tabCount = useMemo(() => {
    const c = { all: rows.length, draft: 0, active: 0, working: 0, completed: 0, closed: 0 };
    rows.forEach(({ st }) => {
      if (isClosedTab(st)) c.closed += 1;
      else if (c[st] != null) c[st] += 1;
    });
    return c;
  }, [rows]);

  const now = Date.now();
  const recent = (list) => list.filter(({ o }) => now - new Date(o.created_at).getTime() <= 30 * 86400000).length;

  const months = useMemo(() => {
    const set = new Set(rows.map(({ o }) => periodKey(o)).filter(Boolean));
    return [...set].sort().reverse();
  }, [rows]);

  const visible = useMemo(() => {
    const q = filters.query.trim().toLowerCase();
    return rows
      .filter(({ o, st }) => {
        if (tab === "closed" ? !isClosedTab(st) : tab !== "all" && st !== tab) return false;
        if (filters.status !== "all" && (filters.status === "closed" ? !isClosedTab(st) : st !== filters.status)) return false;
        if (filters.body !== "all" && Number(o.transport_body_type) !== Number(filters.body)) return false;
        if (filters.period !== "all" && periodKey(o) !== filters.period) return false;
        if (q) {
          const hay = [shortId(o), o.from_address, o.to_address, ORDER_TYPES[o.item?.type], bodyTypeName(o.transport_body_type), o.comment]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          if (!hay.includes(q.replace(/^#/, ""))) return false;
        }
        return true;
      })
      .sort((a, b) => new Date(b.o.created_at) - new Date(a.o.created_at));
  }, [rows, tab, filters]);

  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [tab, filters]);
  const pages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const curPage = Math.min(page, pages - 1);
  const pageRows = useMemo(() => visible.slice(curPage * PAGE_SIZE, (curPage + 1) * PAGE_SIZE), [visible, curPage]);
  const selCount = pageRows.filter(({ o }) => selected[o.uuid]).length;
  const setTab = (k) => setParams(k === "all" ? {} : { tab: k }, { replace: true });
  const reset = () => {
    setFilters({ query: "", status: "all", body: "all", period: "all" });
    setTab("all");
  };

  const closeDialog = () => setDialog(null);
  const routeText = (o) => `${cityOf(o.from_address)} → ${cityOf(o.to_address)}`;

  const run = async (fn, okText, failText) => {
    setBusy(true);
    try {
      await fn();
      closeDialog();
      if (okText) toast(okText);
      await load();
      return true;
    } catch (e) {
      toast(e.message && !e.response ? e.message : apiError(e, failText));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const selectOffer = (o, offer) =>
    run(
      () => acceptOffer(o.uuid, offer.id),
      `Перевозчик выбран: ${offer.carrier?.name || "перевозчик"}. Груз #${shortId(o)} переведён в статус «В работе»`,
      "Не удалось выбрать перевозчика",
    );
  const extend = (o) =>
    run(
      async () => {
        await extendLoad(o.uuid);
        const [m, of] = await Promise.all([fetchMarket(o.uuid).catch(() => null), fetchOffers(o.uuid).catch(() => null)]);
        setCounts((c) => ({ ...c, [o.uuid]: countsOf(m, of) }));
      },
      "Срок актуальности груза продлён",
      "Не удалось продлить груз",
    );
  const closeLoad = (o) => run(() => patchOrder(o.uuid, { status: 2 }), "Груз закрыт", "Не удалось закрыть груз");
  const publishRow = (o) => {
    if (!notExpired(o)) {
      closeDialog();
      toast("Дата погрузки уже прошла — отредактируйте груз перед публикацией");
      return;
    }
    return run(() => patchOrder(o.uuid, { status: 1 }), "Статус изменён на «Активен»", "Не удалось опубликовать груз");
  };
  const rowPrimary = (o, st) => {
    if (st === "draft") return () => setDialog({ k: "publish", o });
    if (st === "active" || st === "working") return () => setDialog({ k: "responses", o });
    return () => navigate(`/order?repeat=${o.uuid}`);
  };

  const metric = (cls, icon, value, label, list) => (
    <article className={`metric ${cls}`}>
      <span className="metric-icon">
        <Icon d={icon} />
      </span>
      <div>
        <b>{orders ? value : "—"}</b>
        <p>{label}</p>
        <small>
          <span>{orders ? `+${recent(list)}` : "—"}</span>за 30 дней
        </small>
      </div>
    </article>
  );
  const by = (st) => rows.filter((r) => r.st === st);

  return (
    <CabPage name="myloads">
      <main id="main">
        <header className="page-heading">
          <div>
            <h1>Мои грузы</h1>
            <p>Управляйте своими грузами. Отслеживайте отклики и находите надежных перевозчиков.</p>
          </div>
          <button className="button primary publish-button" type="button" onClick={() => navigate("/order")}>
            + Опубликовать груз
          </button>
        </header>

        <section className="metrics-row">
          {metric("m0", P.box, tabCount.all, "Всего грузов", rows)}
          {metric("m1", P.arrow, tabCount.active, "Активные грузы", by("active"))}
          {metric("m2", P.truck, tabCount.working, "В работе", by("working"))}
          {metric("m3", P.check, tabCount.completed, "Завершено", by("completed"))}
          {metric("m4", P.weight, tabCount.draft, "Черновики", by("draft"))}
        </section>

        <div aria-label="Статусы грузов" className="load-tabs" role="tablist">
          {TABS.map(([k, label]) => (
            <button key={k} aria-selected={tab === k} className={tab === k ? "active" : ""} role="tab" type="button" onClick={() => setTab(k)}>
              {label} <span>{orders ? tabCount[k] : "…"}</span>
            </button>
          ))}
        </div>

        <form id="load-filters" onSubmit={(e) => e.preventDefault()}>
          <label className="search-input">
            <Icon d={P.search} />
            <input
              aria-label="Поиск по маршруту, грузу, номеру"
              name="query"
              placeholder="Поиск по маршруту, грузу, номеру..."
              value={filters.query}
              onChange={(e) => setFilters((f) => ({ ...f, query: e.target.value }))}
            />
          </label>
          <label className="select-wrap">
            <select aria-label="Статус" name="status" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
              <option value="all">Все статусы</option>
              {Object.entries(STATUS_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
            <Icon d={P.chevron} />
          </label>
          <label className="select-wrap">
            <select aria-label="Тип кузова" name="body" value={filters.body} onChange={(e) => setFilters((f) => ({ ...f, body: e.target.value }))}>
              <option value="all">Все типы кузова</option>
              {BODY_TYPE_OPTIONS.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
            <Icon d={P.chevron} />
          </label>
          <label className="select-wrap date-filter">
            <Icon d={P.cal} />
            <select aria-label="Период" name="period" value={filters.period} onChange={(e) => setFilters((f) => ({ ...f, period: e.target.value }))}>
              <option value="all">За все время</option>
              {months.map((m) => {
                const [y, mo] = m.split("-");
                return (
                  <option key={m} value={m}>
                    {MONTHS_NOM[Number(mo) - 1]} {y}
                  </option>
                );
              })}
            </select>
            <Icon d={P.chevron} />
          </label>
          <button className="button reset" type="button" onClick={reset}>
            Сбросить
          </button>
        </form>

        <div className="table-container">
          <table>
            <colgroup>
              <col style={{ width: "3.5%" }} />
              <col style={{ width: "9.7%" }} />
              <col style={{ width: "14.9%" }} />
              <col style={{ width: "16.6%" }} />
              <col style={{ width: "7.3%" }} />
              <col style={{ width: "8.1%" }} />
              <col style={{ width: "8.3%" }} />
              <col style={{ width: "7.6%" }} />
              <col style={{ width: "6.9%" }} />
              <col style={{ width: "17.1%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>
                  <input
                    aria-label="Выбрать все грузы"
                    type="checkbox"
                    checked={pageRows.length > 0 && selCount === pageRows.length}
                    onChange={(e) => setSelected(e.target.checked ? Object.fromEntries(pageRows.map(({ o }) => [o.uuid, true])) : {})}
                  />
                </th>
                <th>№ груза / Дата</th>
                <th>Маршрут</th>
                <th>Груз / Тип кузова</th>
                <th>Вес / Объем</th>
                <th>Дата загрузки</th>
                <th>Статус</th>
                <th>
                  Подходящих
                  <br />
                  машин
                </th>
                <th>Откликов</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map(({ o, st }) => {
                const c = counts[o.uuid];
                const dash = (v) => (v == null ? "—" : v);
                return (
                  <tr key={o.uuid}>
                    <td>
                      <input
                        aria-label={`Выбрать груз ${shortId(o)}`}
                        className="row-check"
                        type="checkbox"
                        checked={!!selected[o.uuid]}
                        onChange={(e) => setSelected((s) => ({ ...s, [o.uuid]: e.target.checked }))}
                      />
                    </td>
                    <td data-label="№ груза / Дата">
                      <strong>#{shortId(o)}</strong>
                      <small>{fmtDateTime(o.created_at)}</small>
                    </td>
                    <td data-label="Маршрут">
                      <span>
                        {cityOf(o.from_address)} <i className="route-arrow">→</i> {cityOf(o.to_address)}
                      </span>
                      {o.distance ? <small>{o.distance}</small> : null}
                    </td>
                    <td data-label="Груз / Тип кузова">
                      <div className="cargo">
                        <span className="cargo-image" style={{ display: "grid", placeItems: "center", background: "#eaf3ff", borderRadius: 10, color: "#0063ff" }}>
                          <Icon d={P.box} />
                        </span>
                        <div>
                          <strong>{ORDER_TYPES[o.item?.type] || "Груз"}</strong>
                          <small>{bodyTypeName(o.transport_body_type) || "—"}</small>
                        </div>
                      </div>
                    </td>
                    <td data-label="Вес / Объём">
                      {o.item?.weight != null ? `${nf(o.item.weight)} т` : "—"}
                      <small>{o.item?.volume != null ? `${nf(o.item.volume)} м³` : "—"}</small>
                    </td>
                    <td data-label="Дата загрузки">
                      {fmtDateShort(o.from_date)}
                      {o.to_date && o.to_date !== o.from_date ? <small>до {fmtDateShort(o.to_date)}</small> : null}
                    </td>
                    <td data-label="Статус">
                      <span className={`status ${st}`}>
                        <i></i>
                        {STATUS_LABEL[st]}
                      </span>
                      {st === "working" && trips[o.uuid] ? (
                        <small>
                          Везёт: {trips[o.uuid].carrier?.company || trips[o.uuid].driver_name || "перевозчик"}
                          {trips[o.uuid].vehicle?.government_number ? ` · ${trips[o.uuid].vehicle.government_number}` : ""}
                        </small>
                      ) : null}
                    </td>
                    <td data-label="Подходящих машин">
                      <button className="count-button" type="button" onClick={() => setDialog({ k: "matches", o })}>
                        <Icon d={P.truck} />
                        {st === "draft" ? "—" : c ? dash(c.matches) : st === "active" || st === "working" ? "…" : "—"}
                      </button>
                    </td>
                    <td data-label="Откликов">
                      <button className="count-button" type="button" onClick={() => setDialog({ k: "responses", o })}>
                        <Icon d={P.chat} />
                        {st === "draft" ? "—" : c ? dash(c.offers) : st === "active" || st === "working" ? "…" : "—"}
                      </button>
                    </td>
                    <td data-label="Действия">
                      <div className="row-actions">
                        <button
                          className={`button row-action${st === "draft" || st === "active" || st === "working" ? " primary" : ""}`}
                          type="button"
                          onClick={rowPrimary(o, st)}
                        >
                          {st === "draft" ? "Опубликовать" : st === "active" || st === "working" ? "Смотреть отклики" : "Повторить груз"}
                        </button>
                        <button aria-label={`Действия с грузом ${shortId(o)}`} className="button more" type="button" onClick={() => setDialog({ k: "more", o })}>
                          ···
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {orders && visible.length > PAGE_SIZE && (
            <div className="results-more" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: "14px 0" }}>
              <button className="button" type="button" disabled={curPage === 0} onClick={() => setPage(curPage - 1)}>
                ← Назад
              </button>
              <span>
                {curPage * PAGE_SIZE + 1}–{Math.min((curPage + 1) * PAGE_SIZE, visible.length)} из {visible.length} · стр. {curPage + 1}/{pages}
              </span>
              <button className="button" type="button" disabled={curPage >= pages - 1} onClick={() => setPage(curPage + 1)}>
                Вперёд →
              </button>
            </div>
          )}
          {orders && visible.length === 0 && (
            <div className="empty">
              <h3>Грузы не найдены</h3>
              <p>{orders.length ? "Измените параметры поиска." : "Вы пока не публиковали грузов."}</p>
              {orders.length ? (
                <button className="button" type="button" onClick={reset}>
                  Сбросить фильтры
                </button>
              ) : (
                <button className="button primary" type="button" onClick={() => navigate("/order")}>
                  Опубликовать груз
                </button>
              )}
            </div>
          )}
          {!orders && (
            <div className="empty">
              <h3>{error || "Загрузка…"}</h3>
            </div>
          )}
        </div>
        {selCount > 0 && (
          <div id="selection-bar">
            <span>Выбрано грузов: {selCount}</span>
            <button className="button" type="button" onClick={() => setSelected({})}>
              Снять выделение
            </button>
          </div>
        )}
      </main>
      {dialog && (
        <LoadDialog
          dialog={dialog}
          st={effStatus(dialog.o)}
          ttl={counts[dialog.o.uuid]}
          busy={busy}
          routeText={routeText}
          onClose={closeDialog}
          open={setDialog}
          trip={trips[dialog.o.uuid]}
          navigate={navigate}
          actions={{ selectOffer, closeLoad, publishRow, extend }}
        />
      )}
      {toastNode}
      <MobileNav />
    </CabPage>
  );
}

const OFFER_STATUS = { ACCEPTED: "Выбран", REJECTED: "Отклонён", EXPIRED: "Истёк", WITHDRAWN: "Отозван", CANCELLED: "Отменён" };

function useOffers(o, enabled) {
  const [state, setState] = useState({ loading: enabled, data: null, error: null });
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    fetchOffers(o.uuid)
      .then((r) => alive && setState({ loading: false, data: r?.offers || [], error: null }))
      .catch(() => alive && setState({ loading: false, data: null, error: "Не удалось загрузить отклики" }));
    return () => {
      alive = false;
    };
  }, [o.uuid, enabled]);
  return state;
}

const contactName = (d, of) => d?.name || d?.carrier?.name || d?.contact?.name || d?.full_name || of.carrier?.name || "Перевозчик";
const contactPhone = (d) => d?.phone || d?.phone_number || d?.carrier?.phone || d?.contact?.phone || "";

function ResponsesBody({ o, st, busy, routeText, actions }) {
  const { loading, data, error } = useOffers(o, st !== "draft");
  const [confirm, setConfirm] = useState(null); // offer awaiting «Выбрать перевозчика» confirmation
  const [contacts, setContacts] = useState({}); // offerId -> {loading, data, error}
  const showContact = async (of) => {
    setContacts((c) => ({ ...c, [of.id]: { loading: true } }));
    try {
      const d = await revealContact(of.id);
      setContacts((c) => ({ ...c, [of.id]: { data: d } }));
    } catch (e) {
      const msg = e.status === 403 ? "Контакты доступны только автору груза." : e.status === 404 ? "Отклик не найден." : e.message || "Не удалось получить контакты";
      setContacts((c) => ({ ...c, [of.id]: { error: msg } }));
    }
  };
  const cargo = ORDER_TYPES[o.item?.type] || "Груз";
  const head = (
    <p>
      {routeText(o)} · {cargo}
    </p>
  );
  if (st === "draft") {
    return (
      <>
        {head}
        <p>Откликов пока нет. Они появятся после публикации груза.</p>
      </>
    );
  }
  if (loading) return <>{head}<p>Загрузка…</p></>;
  if (error) return <>{head}<p>{error}</p></>;
  if (!data.length) {
    return (
      <>
        {head}
        <p>Откликов пока нет. Они появятся после публикации груза.</p>
      </>
    );
  }
  return (
    <>
      {head}
      {data.map((of) => {
        const t = of.truck || {};
        const cur = of.currency ? ` ${codeSign(of.currency)}` : "";
        const rate = of.amount != null ? `${nf(of.amount)}${cur}` : o.price ? `${nf(o.price)} (по ставке груза)` : "ставка не указана";
        const line1 = [t.body_type, t.max_load_weight != null ? `${nf(t.max_load_weight)} т` : null, rate].filter(Boolean).join(" · ");
        const line2 = [
          t.model && t.model !== t.mark ? [t.mark, t.model].filter(Boolean).join(" ") : t.mark,
          t.government_number,
          of.distance_km != null ? `${nf(of.distance_km)} км до погрузки` : null,
          of.response_seconds != null ? `отклик через ${fmtResponse(of.response_seconds)}` : null,
        ]
          .filter(Boolean)
          .join(" · ");
        return (
          <div className="offer" key={of.id}>
            <strong>{of.carrier?.name || "Перевозчик"}</strong>
            <p>
              {line1}
              {line2 ? <><br />{line2}</> : null}
              {of.status !== "OPEN" ? <><br />Статус: {OFFER_STATUS[of.status] || of.status}</> : null}
            </p>
            {contacts[of.id]?.data ? (
              <p className="offer-contact">
                <strong style={{ display: "inline" }}>{contactName(contacts[of.id].data, of)}</strong>
                {contactPhone(contacts[of.id].data) ? (
                  <>
                    {" · "}
                    <a href={`tel:${contactPhone(contacts[of.id].data).replace(/[^\d+]/g, "")}`}>{contactPhone(contacts[of.id].data)}</a>
                  </>
                ) : (
                  " · телефон не указан"
                )}
              </p>
            ) : null}
            {contacts[of.id]?.error ? <p role="alert" style={{ color: "#c0392b" }}>{contacts[of.id].error}</p> : null}
            <div className="offer-actions">
              {of.status === "OPEN" || of.status === "ACCEPTED" ? (
                <button className="button" type="button" disabled={contacts[of.id]?.loading} onClick={() => showContact(of)}>
                  {contacts[of.id]?.loading ? "Загрузка…" : "Показать контакты"}
                </button>
              ) : null}
              {of.status === "OPEN" && st === "active" ? (
                <button className="button primary" type="button" disabled={busy} onClick={() => setConfirm(of)}>
                  Выбрать перевозчика
                </button>
              ) : null}
            </div>
          </div>
        );
      })}
      {confirm ? (
        <Dialog title="Выбрать перевозчика?" onClose={() => setConfirm(null)}>
          <p>
            <strong>{confirm.carrier?.name || "Перевозчик"}</strong>
            <br />
            Ставка: {confirm.amount != null ? `${nf(confirm.amount)} ${codeSign(confirm.currency) || ""}`.trim() : o.price ? `${nf(o.price)} (по ставке груза)` : "не указана"}
          </p>
          <p className="warn">После выбора груз #{shortId(o)} будет закрыт для других перевозчиков и переведён в статус «В работе». Действие нельзя отменить.</p>
          <div className="actions">
            <button className="button" type="button" onClick={() => setConfirm(null)}>
              Отмена
            </button>
            <button
              className="button primary"
              type="button"
              disabled={busy}
              onClick={async () => {
                const ok = await actions.selectOffer(o, confirm);
                if (!ok) setConfirm(null);
              }}
            >
              {busy ? "Выбираем…" : "Подтвердить выбор"}
            </button>
          </div>
        </Dialog>
      ) : null}
    </>
  );
}

function MatchesBody({ o, st, routeText }) {
  const [s, setS] = useState({ loading: st !== "draft", m: null, error: null });
  useEffect(() => {
    if (st === "draft") return undefined;
    let alive = true;
    fetchMarket(o.uuid)
      .then((m) => alive && setS({ loading: false, m, error: null }))
      .catch(() => alive && setS({ loading: false, m: null, error: "Не удалось получить подбор машин" }));
    return () => {
      alive = false;
    };
  }, [o.uuid, st]);
  if (st === "draft") return <p>Опубликуйте черновик, чтобы запустить подбор машин.</p>;
  if (s.loading) return <p>Загрузка…</p>;
  if (s.error) return <p>{s.error}</p>;
  const mk = s.m?.market || {};
  const th = mk.matched_above_threshold ?? 0;
  const top = (mk.top_trucks || []).filter((t) => (t.score ?? 0) >= th).slice(0, 5);
  return (
    <>
      <p>
        Для груза #{shortId(o)} найдено подходящих машин: {mk.matched_trucks ?? 0}.
      </p>
      <p>{routeText(o)}</p>
      {top.map((t) => (
        <div className="offer" key={t.match_id}>
          <strong>{[t.mark, t.government_number].filter(Boolean).join(" · ") || "Машина"}</strong>
          <p>
            {[t.body_type_name, t.max_load_weight != null ? `${nf(t.max_load_weight)} т` : null, t.city, t.distance_km != null ? `${nf(t.distance_km)} км до погрузки` : null, t.driver_name]
              .filter(Boolean)
              .join(" · ")}
            {t.score != null ? <><br />Совпадение: {t.score}%</> : null}
          </p>
        </div>
      ))}
    </>
  );
}

function LoadDialog({ dialog, st, ttl, busy, trip, routeText, navigate, onClose, open, actions }) {
  const { k, o } = dialog;
  const id = shortId(o);
  const editable = (st === "draft" || st === "active" || st === "expired") && !o.driver;
  if (k === "responses")
    return (
      <SidePanel title={`Отклики на груз #${id}`} subtitle={routeText(o)} onClose={onClose}>
        <ResponsesBody o={o} st={st} busy={busy} routeText={routeText} actions={actions} />
      </SidePanel>
    );
  if (k === "matches")
    return (
      <SidePanel title="Подходящие машины" subtitle={`Груз #${id}`} onClose={onClose}>
        <MatchesBody o={o} st={st} routeText={routeText} />
      </SidePanel>
    );
  if (k === "more")
    return (
      <SidePanel
        title={`Груз #${id}`}
        subtitle={routeText(o)}
        onClose={onClose}
        footer={
          <>
            {editable ? (
              <button className="button" type="button" onClick={() => navigate(`/order?edit=${o.uuid}`)}>
                Редактировать
              </button>
            ) : null}
            <button className="button" type="button" onClick={() => navigate(`/order?repeat=${o.uuid}`)}>
              Повторить
            </button>
            {o.status === 1 && o.delivery_status == null && notExpired(o) ? (
              <button className="button" type="button" disabled={busy} onClick={() => actions.extend(o)}>
                Груз ещё актуален
              </button>
            ) : null}
            {!isClosedTab(st) && st !== "completed" ? (
              <button className="button" type="button" disabled={busy} onClick={() => actions.closeLoad(o)}>
                Закрыть груз
              </button>
            ) : null}
          </>
        }
      >
        {ttl && ttl.ttlLeft != null ? (
          <p className="side-note">
            {ttl.ttlExpired
              ? "Срок актуальности истёк — груз не участвует в подборе. Подтвердите, что он ещё актуален."
              : `Груз актуален ещё ${ttl.ttlLeft >= 60 ? `${Math.floor(ttl.ttlLeft / 60)} ч ${ttl.ttlLeft % 60} мин` : `${ttl.ttlLeft} мин`}.`}
          </p>
        ) : null}
        <SideRows
          rows={[
            ["Статус", STATUS_LABEL[st]],
            ["Маршрут", `${o.from_address || "—"} → ${o.to_address || "—"}`],
            ["Погрузка", [fmtDateShort(o.from_date), o.to_date && o.to_date !== o.from_date ? fmtDateShort(o.to_date) : null].filter((x) => x && x !== "—").join(" — ")],
            ["Груз", ORDER_TYPES[o.item?.type]],
            ["Вес / объём", [o.item?.weight != null ? `${nf(o.item.weight)} т` : null, o.item?.volume != null ? `${nf(o.item.volume)} м³` : null].filter(Boolean).join(" / ")],
            ["Кузов", bodyTypeName(o.transport_body_type)],
            ["Ставка", o.price ? `${nf(o.price)} ${codeSign(o.currency) || ""}`.trim() : null],
            ["Комментарий", o.comment],
          ]}
        />
        {st === "working" || st === "completed" ? (
          trip ? (
            <>
              <PartyCard
                name={trip.carrier?.name || trip.driver_name}
                company={trip.carrier?.company}
                phone={trip.carrier?.phone}
                type="Перевозчик"
                sub={[trip.vehicle?.mark, trip.vehicle?.government_number].filter(Boolean).join(" ")}
              />
              <SideRows rows={[["Согласованная цена", trip.agreed_amount != null ? `${nf(trip.agreed_amount)} ${codeSign(trip.agreed_currency) || ""}`.trim() : null]]} />
            </>
          ) : (
            <p className="side-note">Информация о перевозчике появится после загрузки рейса.</p>
          )
        ) : null}
      </SidePanel>
    );
  if (k === "publish")
    return (
      <Dialog title={`Опубликовать груз #${id}`} onClose={onClose}>
        <p>Груз станет доступен перевозчикам, подбор машин запустится сразу после публикации.</p>
        <button className="button primary" type="button" disabled={busy} onClick={() => actions.publishRow(o)}>
          Подтвердить
        </button>
      </Dialog>
    );
  return null;
}
